import * as Sentry from '@sentry/react';
import { log } from '@utils/log';
import { isSentryRuntimeEnabled } from '../config/sentry';

type Primitive = number | string | boolean | bigint | symbol | null | undefined;
type ErrorLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
type ErrorTags = Record<string, Primitive>;
type ErrorExtras = Record<string, unknown>;

const chunkFailedPatterns = [
  /Failed to load module script:.*MIME type of "text\/html"/i,
  /Loading chunk [\d]+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i
];

function normalizeError(
  error: unknown,
  extras?: ErrorExtras
): { error: Error; extras?: ErrorExtras } {
  if (error instanceof Error) {
    return { error, extras };
  }

  if (typeof error === 'object' && error !== null) {
    const errorKeys = Object.keys(error);
    return {
      error: new Error(`Object captured as exception with keys: ${errorKeys.join(', ')}`),
      extras: {
        ...extras,
        originalError: error,
        errorKeys
      }
    };
  }

  return {
    error: new Error(`Non-Error exception: ${String(error)}`),
    extras: {
      ...extras,
      originalValue: error,
      errorType: typeof error
    }
  };
}

function isChunkLoadError(message: string): boolean {
  return chunkFailedPatterns.some((pattern) => pattern.test(message));
}

function tryRecoverChunkLoadError(): boolean {
  const reloadKey = 'app_reload_timestamp';
  const reloadCooldownMs = 10_000;
  const lastReload = window.localStorage.getItem(reloadKey);
  const now = Date.now();

  if (!lastReload || now - Number.parseInt(lastReload, 10) > reloadCooldownMs) {
    window.localStorage.setItem(reloadKey, now.toString());
    window.location.reload();
    return true;
  }

  log.error('Reload cooldown active. Not reloading to prevent infinite loop.');
  window.setTimeout(() => {
    window.localStorage.removeItem(reloadKey);
  }, reloadCooldownMs);

  return false;
}

export async function handleAsyncError<T>(
  fn: () => Promise<T> | T,
  fallback: T = null as unknown as T,
  errMsg = 'Error'
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    handleError(error, errMsg);
    return fallback;
  }
}

export function handleError(
  error: unknown,
  message: string,
  tag?: ErrorTags,
  level?: ErrorLevel,
  extras?: ErrorExtras
): void {
  const normalized = normalizeError(error, extras);
  const processedError = normalized.error;
  const errorExtras = normalized.extras;
  const errorMessage = processedError.message || String(error);

  if (isChunkLoadError(errorMessage)) {
    log.warn('Detected chunk load error, attempting reload...', errorMessage);
    if (tryRecoverChunkLoadError()) {
      return;
    }
  }

  log.error(message, error);

  if (!isSentryRuntimeEnabled) {
    return;
  }

  Sentry.withScope((scope) => {
    if (tag) {
      Object.entries(tag).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (level) {
      scope.setLevel(level);
    }

    if (message) {
      scope.setExtra('message', message);
    }

    if (errorExtras) {
      Object.entries(errorExtras).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    Sentry.captureException(processedError);
  });
}
